import { Component, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EVENTS,
  EventItem,
  getAllCategories,
  getAllRegions,
} from '../../data/events';
import { ContactSection } from '../../components/contact-section/contact-section';
import { take } from 'rxjs';
import { SeoService } from '../../services/seo.service';
import { createPortfolioOrganizationSchemas } from '../../data/structured-data';

@Component({
  selector: 'app-portfolio',
  imports: [CommonModule, FormsModule, ContactSection],
  templateUrl: './portfolio.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './portfolio.scss',
})
export class Portfolio implements OnInit {
  allEvents: EventItem[] = EVENTS;
  filteredEvents: EventItem[] = EVENTS;

  allCategories: string[] = getAllCategories();
  allRegions: string[] = getAllRegions();

  selectedCategories: string[] = [];
  selectedRegions: string[] = [];

  // Temporary selections before applying
  tempSelectedCategories: string[] = [];
  tempSelectedRegions: string[] = [];

  searchTerm: string = '';
  isFilterVisible: boolean = false;

  currentPage = 1;
  itemsPerPage = 15;
  totalPages = Math.ceil(this.allEvents.length / this.itemsPerPage);

  private readonly seoService = inject(SeoService);
  protected readonly seoTitle = 'Mari Group – Portfolio of Live Experiences';
  protected readonly seoDescription =
    'Explore Mari Group’s global portfolio of iconic live experiences across sport, art, and lifestyle — from Frieze and the Miami Open to Winter Wonderland.';

  constructor(private route: ActivatedRoute, private router: Router) {
    this.seoService.update({
      title: this.seoTitle,
      description: this.seoDescription,
      jsonLdBlocks: createPortfolioOrganizationSchemas(this.allEvents),
      breadcrumbItems: [
        { name: 'Home', path: '/' },
        { name: 'Portfolio', path: '/portfolio' },
      ],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      if (params['category']) {
        const categories = Array.isArray(params['category'])
          ? params['category']
          : [params['category']];
        this.tempSelectedCategories = categories.filter((cat) =>
          this.allCategories.includes(cat)
        );
        this.selectedCategories = [...this.tempSelectedCategories];
      }

      if (params['region']) {
        const regions = Array.isArray(params['region'])
          ? params['region']
          : [params['region']];
        this.tempSelectedRegions = regions.filter((region) =>
          this.allRegions.includes(region)
        );
        this.selectedRegions = [...this.tempSelectedRegions];
      }

      this.applyFilters();
    });
  }

  onCategoryChange(category: string, isChecked: boolean): void {
    if (isChecked) {
      this.tempSelectedCategories.push(category);
    } else {
      this.tempSelectedCategories = this.tempSelectedCategories.filter(
        (c) => c !== category
      );
    }
  }

  onRegionChange(region: string, isChecked: boolean): void {
    if (isChecked) {
      this.tempSelectedRegions.push(region);
    } else {
      this.tempSelectedRegions = this.tempSelectedRegions.filter(
        (r) => r !== region
      );
    }
  }

  removeCategoryFilter(category: string): void {
    this.selectedCategories = this.selectedCategories.filter(
      (c) => c !== category
    );
    this.tempSelectedCategories = this.tempSelectedCategories.filter(
      (c) => c !== category
    );
    this.currentPage = 1;
    this.applyFilters();
  }

  removeRegionFilter(region: string): void {
    this.selectedRegions = this.selectedRegions.filter((r) => r !== region);
    this.tempSelectedRegions = this.tempSelectedRegions.filter(
      (r) => r !== region
    );
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    this.selectedCategories = [...this.tempSelectedCategories];
    this.selectedRegions = [...this.tempSelectedRegions];
    this.currentPage = 1;
    this.filterEvents();
    this.isFilterVisible = false;
    this.updateUrl();
  }

  clearFilters(): void {
    this.tempSelectedCategories = [];
    this.tempSelectedRegions = [];
    this.applyFilters();
  }

  private updateUrl(): void {
    const queryParams: any = {};

    if (this.selectedCategories.length > 0) {
      queryParams['category'] = this.selectedCategories;
    }

    if (this.selectedRegions.length > 0) {
      queryParams['region'] = this.selectedRegions;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.filterEvents();
  }

  goToPreviousPage(): void {
    this.currentPage = this.currentPage - 1;
    this.filterEvents();
    this.goToTop();
  }

  goToNextPage(): void {
    this.currentPage = this.currentPage + 1;
    this.filterEvents();
    this.goToTop();
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.filterEvents();
    this.goToTop();
  }

  isCategorySelected(category: string): boolean {
    return this.tempSelectedCategories.includes(category);
  }

  isRegionSelected(region: string): boolean {
    return this.tempSelectedRegions.includes(region);
  }

  private filterEvents(): void {
    const normalizedSearchTerm = this.normalizeForSearch(this.searchTerm);

    const _filteredEvents = this.allEvents.filter((event) => {
      const categoryMatch =
        this.selectedCategories.length === 0 ||
        this.selectedCategories.includes(event.category);

      const regionMatch =
        this.selectedRegions.length === 0 ||
        event.region
          .flatMap((r) => r.split(',').map((t) => t.trim()))
          .some((r) => this.selectedRegions.includes(r));

      const searchMatch =
        normalizedSearchTerm === '' ||
        this.normalizeForSearch(event.title).includes(normalizedSearchTerm);

      return categoryMatch && regionMatch && searchMatch;
    });

    const skip = (this.currentPage - 1) * this.itemsPerPage;
    const take = this.itemsPerPage;

    this.totalPages = Math.ceil(_filteredEvents.length / this.itemsPerPage);
    this.filteredEvents = _filteredEvents.slice(skip, skip + take);
  }

  private normalizeForSearch(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private goToTop() {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
  }
}
